import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { useSite, ICONS } from "@/context/ContentContext";

const StorySection = () => {
  const D = useSite();
  const story = D.about.story;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  const img2Y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section ref={ref} className="relative mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-36">
      <div className="grid items-start gap-14 md:grid-cols-12">
        <div className="md:col-span-5 md:sticky md:top-32">
          <SectionHeading
            overline={story.overline}
            title={story.title}
            description={story.description}
          />
          <Reveal delay={0.2}>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">{story.paragraph}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <blockquote className="mt-10 border-l-2 border-gold/50 pl-6 font-serif text-xl italic leading-relaxed text-charcoal-800 md:text-2xl">
              "{story.quote}"
            </blockquote>
          </Reveal>
        </div>
        <div className="relative md:col-span-7">
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-[2rem] shadow-lift">
              <motion.img
                style={{ y: imgY }}
                src={D.about.images.main}
                alt="Ada yurdu iç mekanı"
                loading="lazy"
                className="h-[380px] w-full scale-110 object-cover md:h-[520px]"
              />
            </div>
          </Reveal>
          <Reveal delay={0.25} className="relative z-10 -mt-16 ml-auto w-3/4 md:-mt-24">
            <div className="overflow-hidden rounded-[2rem] border-8 border-cream-50 shadow-lift">
              <motion.img
                style={{ y: img2Y }}
                src={D.about.images.students}
                alt="Birlikte çalışan öğrenciler"
                loading="lazy"
                className="h-[240px] w-full scale-110 object-cover md:h-[300px]"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const StatsBand = () => {
  const D = useSite();
  return (
    <section className="bg-charcoal-900 py-20 md:py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-12 px-6 md:px-12 lg:grid-cols-4">
        {D.stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1}>
            <div className="text-center">
              <p className="font-serif text-4xl tracking-tight text-white md:text-5xl">{s.value}</p>
              <p className="mt-3 text-sm text-white/50">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const ValuesSection = () => {
  const D = useSite();
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <SectionHeading
        align="center"
        overline="Değerlerimiz"
        title="Bizi biz yapan *ilkeler*"
      />
      <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {D.values.map((v, i) => {
          const Icon = ICONS[v.icon] || Sparkles;
          return (
            <Reveal key={v.title} delay={(i % 3) * 0.09} className="h-full">
              <div
                data-testid={`value-card-${i}`}
                className="group h-full rounded-3xl border border-charcoal-900/5 bg-white p-8 shadow-premium transition-all duration-500 ease-premium hover:-translate-y-1.5 hover:border-gold/30 hover:shadow-lift"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-500 transition-colors duration-500 group-hover:bg-rose-200">
                  <Icon
                    className="h-6 w-6 transition-transform duration-500 ease-premium group-hover:scale-110"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="mt-6 font-serif text-xl tracking-tight text-charcoal-900">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const LifestyleSection = () => {
  const D = useSite();
  const life = D.about.lifestyle;
  return (
    <section className="bg-gradient-to-b from-cream-100 to-rose-50 py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:grid-cols-2 md:px-12">
        <Reveal>
          <div className="overflow-hidden rounded-[2rem] shadow-lift">
            <img
              src={life.image}
              alt="Sahil ve yaşam alanı"
              loading="lazy"
              className="h-[380px] w-full object-cover transition-transform duration-[1400ms] ease-premium hover:scale-[1.04] md:h-[480px]"
            />
          </div>
        </Reveal>
        <div>
          <SectionHeading
            overline={life.overline}
            title={life.title}
            description={life.description}
          />
          <Reveal delay={0.2}>
            <ul className="mt-8 space-y-4">
              {life.bullets.map((item) => (
                <li key={item} className="flex items-start gap-3.5 text-sm leading-relaxed text-charcoal-800/80">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rotate-45 bg-gold" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function AboutPage() {
  const D = useSite();
  const p = D.pages.hakkimizda;
  return (
    <>
      <PageHero overline={p.overline} titleLines={p.titleLines} description={p.description} />
      <StorySection />
      <StatsBand />
      <ValuesSection />
      <LifestyleSection />
      <div className="pt-8">
        <CTABanner />
      </div>
    </>
  );
}
